import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Header } from "../layout/Header";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { ScrollArea } from "../components/ui/scroll-area";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import {
  Loader2,
  AlertTriangle,
  Users,
  Eye,
  UserX, // Icon for Unassign
} from "lucide-react";
import { formatDate } from "../utils/dateUtils";
import { toast } from "../components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

// Create a dedicated axios instance with base URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "https://isco-backend-deployment-932661235642.us-central1.run.app",
  headers: {
    "Content-Type": "application/json"
  }
});

// Add interceptor to log requests (removed HTTPS forcing)
api.interceptors.request.use((config) => {
  console.log('RsmManagerPage API Request:', config.method?.toUpperCase(), config.url);
  console.log('RSM Manager API Request:', config.method?.toUpperCase(), config.url, 'BaseURL:', config.baseURL);
  return config;
});

// Helper function to calculate progress
const calculateProgress = (schedule) => {
  if (
    !schedule?.itinerary?.customers ||
    schedule.itinerary.customers.length === 0
  ) {
    return 0;
  }
  const customers = schedule.itinerary.customers;
  const completed = customers.filter((c) => c.status === "COMPLETED").length;
  const inProgress = customers.filter((c) => c.status === "IN_PROGRESS").length;
  return Math.round(((completed + inProgress * 0.5) / customers.length) * 100);
};

// Helper for priority badge
const getPriorityBadgeVariant = (priority) => {
  switch (priority?.toLowerCase()) {
    case "high":
      return "destructive";
    case "medium":
      return "secondary";
    case "low":
      return "default"; // Use default (dark bg) for low
    default:
      return "outline";
  }
};

function RsmManagerPage() {
  const [managedRsms, setManagedRsms] = useState([]);
  const [selectedRsmId, setSelectedRsmId] = useState(""); // Store only ID
  const [selectedRsmName, setSelectedRsmName] = useState(""); // Store name for display
  const [rsmSchedules, setRsmSchedules] = useState([]);
  const [loadingRsms, setLoadingRsms] = useState(true);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [unassigningScheduleId, setUnassigningScheduleId] = useState(null); // Track which schedule is being unassigned
  const [error, setError] = useState(null);
  const [pageSize, setPageSize] = useState(50); // Default to 50 items
  const navigate = useNavigate();

  // Fetch managed RSMs on component mount
  useEffect(() => {
    // Set auth token for api instance
    const token = localStorage.getItem('isco_jwt');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    
    const fetchManagedRsms = async () => {
      setLoadingRsms(true);
      setError(null);
      try {
        // --- UPDATED ENDPOINT ---
        console.log("Fetching managed RSMs using GET /users?role=RSM...");
        const response = await api.get("/users", { params: { role: "RSM" } });
        // --- END UPDATED ENDPOINT ---

        console.log("Managed RSMs response:", response.data);

        if (Array.isArray(response.data)) {
          setManagedRsms(response.data);
        } else {
          console.warn(
            "Unexpected response format for managed RSMs:",
            response.data,
          );
          setManagedRsms([]);
        }
      } catch (err) {
        console.error("Error fetching managed RSMs:", err);
        const errorMsg =
          err.response?.data?.error ||
          err.message ||
          "Failed to load managed RSMs.";
        setError(errorMsg); // Set error state for potential display
        // Only show toast for non-404 errors, as 404 might mean no RSMs in region
        if (err.response?.status !== 404) {
          toast({
            title: "Error Loading RSMs",
            description: errorMsg,
            variant: "destructive",
          });
        }
      } finally {
        setLoadingRsms(false);
      }
    };

    fetchManagedRsms();
  }, []); // Run only once on mount

  // Fetch schedules for the selected RSM (memoized with useCallback)
  const fetchSchedulesForRsm = useCallback(async (rsmId, limit = null) => {
    if (!rsmId) {
      setRsmSchedules([]); // Clear schedules if no RSM is selected
      return;
    }
    setLoadingSchedules(true);
    setError(null); // Clear previous schedule errors
    try {
      console.log(`Fetching schedules for RSM ID: ${rsmId}...`);
      // Build query parameters
      const params = new URLSearchParams({ assigned_to_id: rsmId });
      if (limit && limit !== 'all') {
        params.append('limit', limit);
      }
      params.append('sort', 'assigned_at');
      params.append('order', 'desc');
      
      const response = await api.get(`/schedules/?${params.toString()}`);
      console.log(`Schedules response for RSM ${rsmId}:`, response.data);

      if (Array.isArray(response.data)) {
        // Sort by assigned_at in descending order (most recent first) on frontend as well
        const sortedSchedules = response.data.sort((a, b) => {
          const dateA = new Date(a.assigned_at || 0);
          const dateB = new Date(b.assigned_at || 0);
          return dateB - dateA; // Most recent first
        });
        setRsmSchedules(sortedSchedules);
      } else {
        console.warn(
          `Unexpected response format for RSM ${rsmId} schedules:`,
          response.data,
        );
        setRsmSchedules([]);
      }
    } catch (err) {
      console.error(`Error fetching schedules for RSM ${rsmId}:`, err);
      const errorMsg =
        err.response?.data?.error ||
        err.message ||
        `Failed to load schedules for selected RSM.`;
      setError(errorMsg); // Show error specific to schedule loading
      if (err.response?.status !== 404) {
        toast({
          title: "Error Loading Schedules",
          description: errorMsg,
          variant: "destructive",
        });
      }
      setRsmSchedules([]);
    } finally {
      setLoadingSchedules(false);
    }
  }, []); // Empty dependency array, fetch is triggered by select change

  // Handler when an RSM is selected from the dropdown
  const handleRsmSelect = (rsmId) => {
    if (rsmId) {
      const selected = managedRsms.find((rsm) => rsm.id === rsmId);
      setSelectedRsmId(rsmId);
      setSelectedRsmName(
        selected?.full_name || selected?.username || "Selected RSM",
      );
      fetchSchedulesForRsm(rsmId, pageSize === 'all' ? null : pageSize); // Fetch schedules for the newly selected RSM
    } else {
      // Handle case where selection is cleared (e.g., placeholder)
      setSelectedRsmId("");
      setSelectedRsmName("");
      setRsmSchedules([]);
    }
  };

  // Handler to view a specific schedule's details
  const handleViewScheduleDetails = (scheduleId) => {
    navigate(`/schedules/${scheduleId}`);
  };

  // Handler to unassign a schedule
  const handleUnassignSchedule = useCallback(
    async (scheduleId) => {
      if (!scheduleId || !selectedRsmId) return; // Need schedule and RSM context

      if (
        !window.confirm(
          `Are you sure you want to unassign this schedule from ${selectedRsmName}?`,
        )
      ) {
        return;
      }

      setUnassigningScheduleId(scheduleId); // Set loading state for this specific schedule
      setError(null);

      try {
        console.log(
          `Unassigning schedule ${scheduleId} from RSM ${selectedRsmId}...`,
        );
        // Use PUT /schedules/{id} with assigned_to_id: null
        await api.put(`/schedules/${scheduleId}`, { assigned_to_id: null });

        toast({
          title: "Success!",
          description: `Schedule unassigned from ${selectedRsmName}.`,
        });

        // Refresh the schedule list for the *currently selected* RSM
        fetchSchedulesForRsm(selectedRsmId, pageSize === 'all' ? null : pageSize);
      } catch (err) {
        console.error(`Error unassigning schedule ${scheduleId}:`, err);
        const errorMsg =
          err.response?.data?.error ||
          err.message ||
          "Failed to unassign schedule.";
        setError(errorMsg); // Set error state
        toast({
          title: "Unassignment Failed",
          description: errorMsg,
          variant: "destructive",
        });
      } finally {
        setUnassigningScheduleId(null); // Clear loading state
      }
    },
    [selectedRsmId, selectedRsmName, fetchSchedulesForRsm, pageSize],
  ); // Add dependencies

  return (
    <div>
      <Header /> {/* Using the existing Header */}
      {/* RSM Selection Dropdown */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" /> Select RSM to Manage
          </CardTitle>
          <CardDescription>
            Choose an RSM from the list to view and manage their assigned
            schedules.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingRsms ? (
            <div className="flex items-center text-muted-foreground">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading RSMs...
            </div>
          ) : error && managedRsms.length === 0 ? ( // Show error if RSM loading failed and list is empty
            <div className="text-red-500 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> {error}
            </div>
          ) : managedRsms.length > 0 ? (
            <Select onValueChange={handleRsmSelect} value={selectedRsmId}>
              <SelectTrigger className="w-full md:w-[300px]">
                {/* Placeholder is shown when selectedRsmId is "" */}
                <SelectValue placeholder="Select an RSM..." />
              </SelectTrigger>
              <SelectContent>
                {/* REMOVED: <SelectItem value="">-- Select an RSM --</SelectItem> */}
                {managedRsms.map((rsm) => (
                  <SelectItem key={rsm.id} value={rsm.id}>
                    {rsm.full_name || rsm.username}
                    {rsm.email ? ` (${rsm.email})` : ""}{" "}
                    {/* Conditionally add email */}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <p className="text-muted-foreground">
              No managed RSMs found in your region.
            </p> // Updated placeholder text
          )}
        </CardContent>
      </Card>
      {/* Selected RSM's Schedules Panel */}
      <Card>
        <CardHeader className="p-4 border-b">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="text-lg font-semibold">
                {selectedRsmId ? `${selectedRsmName}'s Schedules` : "Schedules"}
              </CardTitle>
              <CardDescription>
                {selectedRsmId
                  ? `Viewing ${rsmSchedules.length} schedule(s) assigned to ${selectedRsmName}.`
                  : "Select an RSM above to view their assigned schedules."}
              </CardDescription>
            </div>
            {selectedRsmId && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Show:</span>
                <Select 
                  value={pageSize.toString()} 
                  onValueChange={(value) => {
                    setPageSize(value === 'all' ? 'all' : parseInt(value));
                    fetchSchedulesForRsm(selectedRsmId, value === 'all' ? null : parseInt(value));
                  }}
                >
                  <SelectTrigger className="w-[100px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="200">200</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-4 space-y-4">
          {loadingSchedules ? (
            <div className="py-10 text-center text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading schedules...
            </div>
          ) : error && rsmSchedules.length === 0 && selectedRsmId ? ( // Show schedule error only if an RSM is selected
            <div className="p-4 text-center text-red-500 flex items-center justify-center gap-2">
              <AlertTriangle className="h-4 w-4" /> {error}
            </div>
          ) : !selectedRsmId ? (
            <div className="py-10 text-center text-muted-foreground">
              Please select an RSM from the dropdown above.
            </div>
          ) : rsmSchedules.length > 0 ? (
            // Schedule List for the selected RSM
            <ScrollArea className="h-[600px]">
              <div className="space-y-3 pr-4">
                {rsmSchedules.map((schedule) => {
                  const progress = calculateProgress(schedule);
                  const isUnassigning =
                    unassigningScheduleId === schedule.cluster;
                  return (
                    <Card
                      key={schedule.cluster}
                      className="overflow-hidden hover:shadow-md transition-shadow flex flex-col sm:flex-row" // Use flex layout
                    >
                      {/* Schedule Info */}
                      <div className="flex-grow p-3">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-medium text-sm flex-1 mr-2">
                            {schedule.metadata?.itinerary_name ||
                              `Schedule ${schedule.cluster}`}
                          </h4>
                          <Badge
                            variant={getPriorityBadgeVariant(schedule.priority)}
                            className="text-xs flex-shrink-0 px-1.5 py-0.5" // Smaller badge
                          >
                            {schedule.priority || "N/A"}
                          </Badge>
                        </div>

                        <p className="text-xs text-muted-foreground mb-2">
                          {schedule.itinerary?.customers?.length || 0} stops
                        </p>
                        <div className="text-xs text-muted-foreground mb-2">
                          Assigned: {formatDate(schedule.assigned_at)}
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs">
                            <span>Progress</span>
                            <span>{progress}%</span>
                          </div>
                          <Progress value={progress} className="h-2" />
                        </div>
                      </div>

                      {/* Actions - aligned to the right */}
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 p-3 bg-muted/20 border-t sm:border-t-0 sm:border-l">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 px-3 text-xs" // Adjusted size & text
                          onClick={() =>
                            handleViewScheduleDetails(schedule.cluster)
                          }
                          disabled={isUnassigning} // Disable while unassigning
                        >
                          <Eye className="h-3.5 w-3.5 mr-1" /> View
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="h-8 px-3 text-xs" // Adjusted size & text
                          onClick={() =>
                            handleUnassignSchedule(schedule.cluster)
                          }
                          disabled={isUnassigning} // Disable button while loading
                        >
                          {isUnassigning ? (
                            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                          ) : (
                            <UserX className="h-3.5 w-3.5 mr-1" />
                          )}
                          {isUnassigning ? "Unassigning..." : "Unassign"}
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </ScrollArea>
          ) : (
            // No Schedules for selected RSM
            <div className="py-10 text-center text-muted-foreground">
              No schedules currently assigned to {selectedRsmName}.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default RsmManagerPage;
